import type { RequiredSignupFields } from "../config/types";
import { hasValidPhone } from "./checkout-math";

/**
 * The org's required-signup-fields policy, applied inside embed checkout.
 *
 * PLAT-4456 added `Org.attributes.requiredSignupFields` and enforced it in
 * `AuthService.validateRequiredSignupFields`. Embed customers never reach that
 * path: guest checkout provisions its roster through `roster/create-guest`, so
 * the org policy has to be applied here as well as on the server. The server is
 * still authoritative — this module exists so the customer is asked for the
 * data BEFORE paying, instead of being rejected afterwards.
 */

/** What the customer has typed for the org-required profile fields. */
export interface RequiredProfileDraft {
  dateOfBirth: string;
  emergencyContactName: string;
  emergencyContactPhone: string;
}

export function emptyRequiredProfile(): RequiredProfileDraft {
  return { dateOfBirth: "", emergencyContactName: "", emergencyContactPhone: "" };
}

/**
 * Normalize the config field, which is optional on the wire (see
 * `EmbedConfig.requiredSignupFields`) — an older cached config payload, or an
 * org that never opened the setting, means "nothing extra required".
 */
export interface ResolvedRequiredSignupFields {
  dateOfBirth: boolean;
  emergencyContact: boolean;
  phoneNumber: boolean;
}

export function resolveRequiredSignupFields(
  fields: RequiredSignupFields | undefined,
): ResolvedRequiredSignupFields {
  return {
    dateOfBirth: fields?.dateOfBirth === true || fields?.minimumAge !== undefined,
    emergencyContact: fields?.emergencyContact === true,
    phoneNumber: fields?.phoneNumber === true,
  };
}

/**
 * Whether a phone must be entered before checkout can advance.
 *
 * Two independent sources, ORed: the per-embed `options.requirePhone` toggle
 * the embedding site owns, and the org's `requiredSignupFields.phoneNumber`.
 * The org's is the one the API enforces, so it can never be turned off by an
 * embed that leaves `requirePhone` unset.
 */
export function isPhoneRequired(
  optionRequirePhone: boolean,
  fields: RequiredSignupFields | undefined,
): boolean {
  return optionRequirePhone || resolveRequiredSignupFields(fields).phoneNumber;
}

/**
 * Field keys still missing from the draft, in the order they are rendered.
 * Empty means the org's policy is satisfied.
 */
export function missingRequiredProfileFields(
  fields: RequiredSignupFields | undefined,
  draft: RequiredProfileDraft,
): Array<"dateOfBirth" | "emergencyContactName" | "emergencyContactPhone"> {
  const required = resolveRequiredSignupFields(fields);
  const missing: Array<
    "dateOfBirth" | "emergencyContactName" | "emergencyContactPhone"
  > = [];
  if (required.dateOfBirth && dateOfBirthError(fields, draft.dateOfBirth)) {
    missing.push("dateOfBirth");
  }
  if (required.emergencyContact) {
    if (!draft.emergencyContactName.trim()) missing.push("emergencyContactName");
    // Reuse the same phone predicate the rest of checkout gates on, so an
    // emergency contact cannot be saved as "555" when the buyer's own phone
    // in the very same form would have been rejected.
    if (!hasValidPhone(draft.emergencyContactPhone)) {
      missing.push("emergencyContactPhone");
    }
  }
  return missing;
}

/**
 * Customer-facing message per missing field. Kept beside the predicate so a new
 * required field cannot be added to one without the other.
 */
export const REQUIRED_PROFILE_ERRORS: Record<
  "dateOfBirth" | "emergencyContactName" | "emergencyContactPhone",
  string
> = {
  dateOfBirth: "Enter your date of birth — this studio requires it.",
  emergencyContactName:
    "Enter an emergency contact name — this studio requires it.",
  emergencyContactPhone:
    "Enter a valid emergency contact phone; include + and a country code if needed.",
};

export function dateOfBirthError(
  fields: RequiredSignupFields | undefined,
  value: string,
  now: Date = new Date(),
): string | null {
  if (!fields?.dateOfBirth && fields?.minimumAge === undefined) return null;
  if (!value.trim()) return REQUIRED_PROFILE_ERRORS.dateOfBirth;
  if (!isCompleteDate(value)) return 'Enter a valid date of birth.';
  if (fields?.minimumAge === undefined) return null;
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: fields.ageTimeZone || 'UTC',
    year: 'numeric', month: '2-digit', day: '2-digit',
  }).formatToParts(now);
  const part = (type: string) => Number(parts.find((item) => item.type === type)?.value);
  const todayYear = part('year');
  const todayMonth = part('month');
  const todayDay = part('day');
  const today = `${todayYear}-${String(todayMonth).padStart(2, '0')}-${String(todayDay).padStart(2, '0')}`;
  if (value > today) return 'Date of birth cannot be in the future.';
  const [year = 0, month = 0, day = 0] = value.split('-').map(Number);
  const age = todayYear - year - (todayMonth < month || (todayMonth === month && todayDay < day) ? 1 : 0);
  if (age > 120) return 'Enter a valid date of birth.';
  if (age < fields.minimumAge) return `You must be at least ${fields.minimumAge} years old to sign up.`;
  return null;
}

export function isRequiredProfileComplete(
  fields: RequiredSignupFields | undefined,
  draft: RequiredProfileDraft,
): boolean {
  return missingRequiredProfileFields(fields, draft).length === 0;
}

/**
 * A date input yields '' or a complete 'YYYY-MM-DD'. Reject anything else
 * rather than forwarding a partial value: the API drops an unparseable DOB
 * (`new Date('2024-02-30')` rolls over to March 1 instead of failing), so a bad
 * value here would pass the required check and then persist nothing.
 */
function isCompleteDate(value: string): boolean {
  const trimmed = value.trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return false;
  const [yearPart, monthPart, dayPart] = trimmed.split("-");
  const year = Number(yearPart);
  const month = Number(monthPart);
  const day = Number(dayPart);
  const parsed = new Date(Date.UTC(year, month - 1, day));
  return (
    parsed.getUTCFullYear() === year &&
    parsed.getUTCMonth() === month - 1 &&
    parsed.getUTCDate() === day
  );
}

/**
 * Fold a caller-supplied profile into a locally-typed one.
 *
 * The org's fields are collected wherever the buyer's details are collected —
 * which is usually the HOST (booking/purchase/events step 1), not the payment
 * element that ends up minting the guest roster. The host hands them over on
 * `customer`, so whichever half actually asked the question has to win: a
 * locally-typed value first (the payment element rendered the field itself),
 * otherwise the one that arrived with the customer.
 */
export function mergeRequiredProfile(
  draft: RequiredProfileDraft,
  from: Partial<RequiredProfileDraft> | undefined,
): RequiredProfileDraft {
  const pick = (key: keyof RequiredProfileDraft) =>
    draft[key].trim() ? draft[key] : (from?.[key] ?? "");
  return {
    dateOfBirth: pick("dateOfBirth"),
    emergencyContactName: pick("emergencyContactName"),
    emergencyContactPhone: pick("emergencyContactPhone"),
  };
}

/** Payload fields for `roster/create-guest`, omitting anything left blank. */
export function requiredProfilePayload(draft: RequiredProfileDraft): {
  dateOfBirth?: string;
  emergencyContactName?: string;
  emergencyContactPhone?: string;
} {
  const dateOfBirth = draft.dateOfBirth.trim();
  const emergencyContactName = draft.emergencyContactName.trim();
  const emergencyContactPhone = draft.emergencyContactPhone.trim();
  return {
    ...(isCompleteDate(dateOfBirth) ? { dateOfBirth } : {}),
    ...(emergencyContactName ? { emergencyContactName } : {}),
    ...(emergencyContactPhone ? { emergencyContactPhone } : {}),
  };
}
